import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reminderPreferencesAPI } from '../services/api';

type GlobalProps = {
  variant: 'global';
};

type CourseProps = {
  variant: 'course';
  courseId: string;
};

type Props = GlobalProps | CourseProps;

const ReminderEmailSettings: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [globalDisabled, setGlobalDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (props.variant === 'global') {
          const data = await reminderPreferencesAPI.getGlobal();
          setEnabled(data.reminderEmailsEnabled);
        } else {
          const data = await reminderPreferencesAPI.getCourse(props.courseId);
          setEnabled(data.reminderEmailsEnabled);
          setGlobalDisabled(!data.globalReminderEmailsEnabled);
        }
      } catch {
        setError(t('reminderEmails.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [props, t]);

  const handleToggle = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      if (props.variant === 'global') {
        const data = await reminderPreferencesAPI.updateGlobal(next);
        setEnabled(data.reminderEmailsEnabled);
      } else {
        const data = await reminderPreferencesAPI.updateCourse(props.courseId, next);
        setEnabled(data.reminderEmailsEnabled);
        setGlobalDisabled(!data.globalReminderEmailsEnabled);
      }
    } catch {
      setError(t('reminderEmails.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const disabled = saving || (props.variant === 'course' && globalDisabled);

  return (
    <Box className="reminder-emails__settings">
      <Typography variant="subtitle1" className="reminder-emails__title">
        {props.variant === 'global'
          ? t('reminderEmails.globalTitle')
          : t('reminderEmails.courseTitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {props.variant === 'course' && globalDisabled && (
        <Typography variant="body2" className="reminder-emails__toggle-description">
          {t('reminderEmails.globalDisabledHint')}
        </Typography>
      )}

      {!(props.variant === 'course' && globalDisabled) && (
        <Typography variant="body2" className="reminder-emails__toggle-description">
          {props.variant === 'global'
            ? t('reminderEmails.toggleDescription')
            : t('reminderEmails.courseToggleDescription')}
        </Typography>
      )}

      <FormControlLabel
        className="reminder-emails__toggle-row"
        control={
          <Switch
            checked={enabled && !globalDisabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={disabled}
          />
        }
        label={t('reminderEmails.toggleLabel')}
      />
    </Box>
  );
};

export default ReminderEmailSettings;
